import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import type { DashboardLayout, DashboardWidget } from '@sea-snag/shared';
import { useMemo, useState } from 'react';

import { useCanSeeAdmin } from '../../hooks/use-can-see-admin';
import { reorderWidgets } from '../../stores/dashboard-store';
import { visibleWidgets } from './widget-registry';
import { WidgetPreview, WidgetWrapper } from './widget-wrapper';

// ── Dashboard Grid ───────────────────────────────────────────────────

interface DashboardGridProps {
  layout: DashboardLayout;
  isEditMode: boolean;
}

export function DashboardGrid({ layout, isEditMode }: DashboardGridProps) {
  const [activeWidget, setActiveWidget] = useState<DashboardWidget | null>(null);
  const canSeeAdmin = useCanSeeAdmin();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Every user created so far got DEFAULT_LAYOUT, which includes the revenue
  // and occupancy widgets — so a member's *saved* dashboard already holds
  // widgets whose data the API now refuses them. Filtering the catalog alone
  // would leave those cards failing on every refresh.
  const allowedTypes = useMemo(
    () => new Set(visibleWidgets(canSeeAdmin).map((w) => w.type)),
    [canSeeAdmin]
  );

  const sortedWidgets = useMemo(
    () =>
      [...layout.widgets]
        .filter((w) => allowedTypes.has(w.type))
        .sort((a, b) => a.position - b.position),
    [layout.widgets, allowedTypes]
  );

  const widgetIds = useMemo(() => sortedWidgets.map((w) => w.id), [sortedWidgets]);

  function handleDragStart(event: DragStartEvent) {
    const widget = sortedWidgets.find((w) => w.id === event.active.id);
    setActiveWidget(widget ?? null);
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveWidget(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    reorderWidgets(active.id as string, over.id as string);
  }

  function handleDragCancel() {
    setActiveWidget(null);
  }

  if (sortedWidgets.length === 0) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3">
        <p className="text-muted-foreground text-sm">
          No widgets added yet. Click &quot;Add Widget&quot; to get started.
        </p>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={widgetIds}>
        <div className="grid grid-cols-12 gap-4">
          {sortedWidgets.map((widget) => (
            <WidgetWrapper key={widget.id} widget={widget} isEditMode={isEditMode} />
          ))}
        </div>
      </SortableContext>
      <DragOverlay dropAnimation={{ duration: 200, easing: 'ease' }}>
        {activeWidget ? <WidgetPreview widget={activeWidget} /> : null}
      </DragOverlay>
    </DndContext>
  );
}
